import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { ArrowsPointingInIcon, ArrowsPointingOutIcon, PrinterIcon } from '@heroicons/react/24/outline'
import { healthCyclesAPI } from '../../../services/api'
import CycleDocumentBody from './CycleDocumentBody'
import { documentLanguage } from './documentFormat'
import { beginDocumentPrint, endDocumentPrint } from './printDocument'
import { formatDate } from '../cycleStatus'
import './print-document.css'

// VH-228 phase 0: one stored cycle document in the UI language (translated on demand by the API),
// with a Print action that prints the complete document without the app around it.
export function CycleDocumentViewer({ cycleId, documentId }) {
  const { t, i18n } = useTranslation()
  const language = documentLanguage(i18n.language)
  const [printRoot, setPrintRoot] = useState(null)
  const [expanded, setExpanded] = useState(false)

  const query = useQuery({
    queryKey: ['cycleDocument', cycleId, documentId, language],
    queryFn: () => healthCyclesAPI.getDocument(cycleId, documentId, language),
    select: (response) => response.data,
    enabled: !!cycleId && !!documentId,
    staleTime: 5 * 60 * 1000,
  })
  const doc = query.data

  useEffect(() => {
    if (!printRoot) return undefined
    const done = () => setPrintRoot(null)
    window.addEventListener('afterprint', done)
    // Let the portal paint before the print dialog snapshots the page.
    const timer = window.setTimeout(() => window.print(), 50)
    return () => {
      window.clearTimeout(timer)
      window.removeEventListener('afterprint', done)
      endDocumentPrint()
    }
  }, [printRoot])

  useEffect(() => {
    if (!expanded) return undefined
    const onKey = (event) => { if (event.key === 'Escape') setExpanded(false) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [expanded])

  if (query.isLoading) return <p className="text-sm text-gray-500">{t('cycleDocuments.loading')}</p>
  if (query.isError || !doc) return <p className="text-sm text-red-600" role="alert">{t('cycleDocuments.loadFailed')}</p>

  const header = (
    <div className="space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">
        {t(`cycleDocuments.kinds.${doc.kind}`)}
        {doc.week_start && ` · ${t('cycleDocuments.weekOf', { date: formatDate(doc.week_start, i18n.language) })}`}
        {` · ${t(`cycleDocuments.sources.${doc.source}`)} ${formatDate(doc.created_at, i18n.language)}`}
      </p>
      <h2 className="text-xl font-semibold text-gray-900">{doc.title}</h2>
    </div>
  )

  return (
    <article data-testid="cycle-document-viewer"
      className={expanded ? 'fixed inset-0 z-[100] overflow-y-auto bg-surface px-4 py-6 sm:px-8' : ''}
      aria-modal={expanded || undefined} role={expanded ? 'dialog' : undefined}>
      <div className={`space-y-4 ${expanded ? 'max-w-5xl mx-auto' : ''}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        {header}
        <div className="flex flex-wrap gap-2 self-start">
          <button type="button" className="btn-outline text-sm inline-flex items-center gap-1" onClick={() => setExpanded((v) => !v)}>
            {expanded ? <ArrowsPointingInIcon className="h-4 w-4" /> : <ArrowsPointingOutIcon className="h-4 w-4" />}
            {expanded ? t('cycleDocuments.closeFullView') : t('cycleDocuments.openFullView')}
          </button>
          <button type="button" className="btn-outline text-sm inline-flex items-center gap-1"
            onClick={() => setPrintRoot(beginDocumentPrint(i18n.language))}>
            <PrinterIcon className="h-4 w-4" />
            {t('cycleDocuments.print')}
          </button>
        </div>
      </div>
      {doc.translation_status === 'unavailable' && (
        <p className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900" role="status">
          {t('cycleDocuments.translationUnavailable', { language: t(`cycleDocuments.languages.${doc.content_language}`, doc.content_language) })}
        </p>
      )}
      <CycleDocumentBody content={doc.content} />
      {printRoot && createPortal(
        <div className="bg-surface-container p-0">
          <div className="mb-6">{header}</div>
          <CycleDocumentBody content={doc.content} />
        </div>,
        printRoot,
      )}
      </div>
    </article>
  )
}

export default CycleDocumentViewer
